import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import API, { resolveMediaUrl, getBackendBaseUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  PaperPlaneTilt, Paperclip, X, ArrowLeft, MagnifyingGlass,
  WhatsappLogo, CheckCircle, ArrowClockwise, ChatCircleText,
  AddressBook, PencilSimple, UserCircle, Check, Eye, DownloadSimple, ArrowSquareOut, FileText,
  EyeSlash, Prohibit, Trash, ShieldCheck, Warning, XCircle, Image
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import {
  WhatsApp24hHeaderBadge,
  WhatsApp24hListBadge,
  WhatsApp24hExpiredAlert,
  useWhatsApp24hTimer
} from '../components/WhatsApp24hTimer';

const BEEP_FREQ = 880;
function playBeep() {
  try {
    const c = new (window.AudioContext || window.webkitAudioContext)();
    const o = c.createOscillator();
    const g = c.createGain();
    o.connect(g);
    g.connect(c.destination);
    o.frequency.value = BEEP_FREQ;
    o.type = 'sine';
    o.gain.value = 0.2;
    o.start();
    o.stop(c.currentTime + 0.12);
  } catch (e) { /* silent */ }
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return ''; }
}
function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

function WhatsAppImageBubble({ mediaUrl, m, setPreviewMedia, onRetry }) {
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(Boolean(mediaUrl));
  const [attemptedFallback, setAttemptedFallback] = useState(false);

  useEffect(() => {
    setLoadError(false);
    setLoading(Boolean(mediaUrl));
    setAttemptedFallback(false);
  }, [mediaUrl]);

  const displayName = (!m.file_name || m.file_name.startsWith('?') || m.file_name.includes('mid=')) ? 'Photo' : m.file_name;
  const backendBase = getBackendBaseUrl();
  const fallbackProxy = m.id ? (backendBase ? `${backendBase}/api/whatsapp/messages/${m.id}/media` : `/api/whatsapp/messages/${m.id}/media`) : null;
  const activeUrl = (loadError && !attemptedFallback && fallbackProxy && mediaUrl !== fallbackProxy) ? fallbackProxy : mediaUrl;

  const handleImageError = () => {
    if (!attemptedFallback && fallbackProxy && mediaUrl !== fallbackProxy) {
      setAttemptedFallback(true);
      setLoading(true);
      return;
    }
    setLoading(false);
    setLoadError(true);
  };

  if (!activeUrl || (loadError && attemptedFallback)) {
    return (
      <div className="my-1.5 p-2.5 rounded-lg bg-black/5 dark:bg-white/5 border border-dashed border-emerald-500/30 max-w-[280px]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 flex items-center justify-center shrink-0">
              <Image size={20} weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate text-foreground">{displayName}</p>
              <p className="text-[10px] text-muted-foreground">{loadError ? 'Image load issue' : 'Photo received'}</p>
            </div>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={() => onRetry(m.id)}
              className="text-[11px] font-semibold px-2 py-1 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 flex items-center gap-1 shrink-0"
              title="Load / Reload Photo"
            >
              <ArrowClockwise size={12} />
              <span>Load</span>
            </button>
          )}
        </div>
        {mediaUrl && (
          <div className="mt-1.5 pt-1.5 border-t border-black/10 dark:border-white/10 flex items-center justify-between">
            <a
              href={mediaUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-0.5"
            >
              Open link <ArrowSquareOut size={11} />
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="relative group my-1 overflow-hidden rounded-lg cursor-pointer max-w-[280px] bg-black/5 dark:bg-white/5 min-h-[140px] flex items-center justify-center"
      onClick={() => setPreviewMedia({ url: activeUrl, type: 'image', name: displayName })}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center gap-1.5 text-xs text-muted-foreground bg-muted/40 animate-pulse">
          <Image size={18} className="animate-spin text-emerald-600" />
          <span>Loading photo...</span>
        </div>
      )}
      <img
        src={activeUrl}
        alt="WhatsApp Photo"
        className={`rounded-lg max-w-full max-h-[280px] object-cover transition-all duration-200 group-hover:scale-105 ${loading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setLoading(false)}
        onError={handleImageError}
      />
      {!loading && (
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white">
          <Eye size={18} className="drop-shadow" />
          <span className="text-xs font-medium drop-shadow">View Photo</span>
        </div>
      )}
    </div>
  );
}

export default function WhatsAppChatPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'open';
  const [tab, setTab] = useState(initialTab === 'assigned' ? 'assigned' : (initialTab === 'unassigned' ? 'unassigned' : 'open'));
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adminList, setAdminList] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [showList, setShowList] = useState(true);
  const [assignedUnreadCount, setAssignedUnreadCount] = useState(0);
  const prevAssignedCountRef = useRef(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  const [contactBookOpen, setContactBookOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [bookContacts, setBookContacts] = useState([]);
  const [bookSearch, setBookSearch] = useState('');
  const [loadingBook, setLoadingBook] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [inlineEditing, setInlineEditing] = useState(false);
  const [inlineName, setInlineName] = useState('');

  useEffect(() => {
    setInlineEditing(false);
    setInlineName(selected?.name || selected?.phone || '');
  }, [selected?.id]);

  // 24h Window timer for currently selected contact
  const selectedTimer = useWhatsApp24hTimer(selected?.last_incoming_time || selected?.last_time);

  const fetchAssignedUnread = useCallback(() => {
    API.get('/chat/assigned-unread')
      .then(r => {
        const count = r.data.unread || 0;
        if (count > prevAssignedCountRef.current && prevAssignedCountRef.current > 0) {
          playBeep();
        }
        prevAssignedCountRef.current = count;
        setAssignedUnreadCount(count);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchAssignedUnread();
    const t = setInterval(fetchAssignedUnread, 5000);
    return () => clearInterval(t);
  }, [fetchAssignedUnread]);

  // WhatsApp Number Inactivity / Red Alert watchdog state
  const [inactivityAlerts, setInactivityAlerts] = useState({ apis: [], has_red_alerts: false, red_alert_count: 0 });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(false);
  const canDeleteChat = user?.role === 'super_admin' || user?.permissions?.includes('delete_chat');
  const [confirmBlockOpen, setConfirmBlockOpen] = useState(false);
  const [blockingUser, setBlockingUser] = useState(false);

  const fetchInactivityAlerts = useCallback(async () => {
    try {
      const r = await API.get('/whatsapp/inactivity-alerts');
      setInactivityAlerts(r.data || { apis: [], has_red_alerts: false, red_alert_count: 0 });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchInactivityAlerts();
    const t = setInterval(fetchInactivityAlerts, 20000);
    return () => clearInterval(t);
  }, [fetchInactivityAlerts]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'assigned') setTab('assigned');
    else if (t === 'unassigned') setTab('unassigned');
    else if (t === 'closed') setTab('closed');
    else if (t === 'hidden') setTab('hidden');
    else if (t === 'blocked') setTab('blocked');
    else if (t === 'open') setTab('open');
  }, [searchParams]);

  const loadContacts = useCallback(async () => {
    try {
      const beStatus = (tab === 'assigned' || tab === 'unassigned') ? 'open' : tab;
      const r = await API.get('/whatsapp/contacts', { params: { status: beStatus } });
      setContacts(r.data.contacts || []);
    } catch { /* ignore */ }
  }, [tab]);

  // Hide active chat window
  const handleHideChatView = () => {
    setSelected(null);
    setShowList(true);
  };

  // Toggle hiding contact from the list
  const toggleHideContact = async (contactId) => {
    const target = contactId ? contacts.find(c => c.id === contactId) : selected;
    if (!target) return;
    try {
      if (target.is_hidden) {
        await API.post(`/whatsapp/contacts/${target.id}/unhide`);
        toast.success('Chat unhidden from list');
      } else {
        await API.post(`/whatsapp/contacts/${target.id}/hide`);
        toast.success('Chat hidden from list');
        if (selected?.id === target.id) {
          setSelected(null);
          setShowList(true);
        }
      }
      loadContacts();
    } catch {
      toast.error('Failed to update chat visibility');
    }
  };

  // Delete contact permanently (Super Admin only)
  const handleDeleteContact = async () => {
    if (!selected) return;
    setDeletingContact(true);
    try {
      await API.delete(`/whatsapp/contacts/${selected.id}`);
      toast.success('Contact and chat history deleted successfully');
      setConfirmDeleteOpen(false);
      setSelected(null);
      setShowList(true);
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete contact');
    } finally {
      setDeletingContact(false);
    }
  };

  // Block contact (Super Admin only)
  const handleBlockUser = async () => {
    if (!selected) return;
    setBlockingUser(true);
    try {
      await API.post(`/whatsapp/contacts/${selected.id}/block`);
      toast.success(`User +${selected.phone} blocked. Future incoming messages will be dropped.`);
      setConfirmBlockOpen(false);
      setSelected(prev => ({ ...prev, is_blocked: true }));
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block user');
    } finally {
      setBlockingUser(false);
    }
  };

  const handleRetryMedia = async (msgId) => {
    try {
      toast.loading('Fetching photo from WhatsApp...', { id: 'retry-photo' });
      const { data } = await API.post(`/whatsapp/messages/${msgId}/retry-media`);
      if (data.media_url) {
        toast.success('Photo loaded successfully!', { id: 'retry-photo' });
        setMessages(prev => prev.map(msg => msg.id === msgId ? {
          ...msg,
          media_url: data.media_url,
          file_url: data.file_url || data.media_url,
          file_name: data.file_name || (msg.file_name && !msg.file_name.startsWith('?') && !msg.file_name.includes('mid=') ? msg.file_name : 'Photo.jpg'),
          has_media: true
        } : msg));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not retrieve photo', { id: 'retry-photo' });
    }
  };

  // Unblock contact (Super Admin only)
  const handleUnblockUser = async () => {
    if (!selected) return;
    setBlockingUser(true);
    try {
      await API.post(`/whatsapp/contacts/${selected.id}/unblock`);
      toast.success(`User +${selected.phone} unblocked.`);
      setSelected(prev => ({ ...prev, is_blocked: false }));
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unblock user');
    } finally {
      setBlockingUser(false);
    }
  };

  const fetchAdminList = useCallback(() => {
    API.get('/chat/assignable-users')
      .then(r => setAdminList(r.data.users || []))
      .catch(() => {
        API.get('/staff').then(r => setAdminList(r.data.staff || [])).catch(() => {});
      });
  }, []);

  useEffect(() => {
    loadContacts();
    fetchAdminList();
    const t = setInterval(loadContacts, 2500);
    return () => clearInterval(t);
  }, [loadContacts, fetchAdminList]);

  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const hasHistoryPushedRef = useRef(false);

  // Handle phone / browser hardware back button and ESC key to return to contact list
  const handleBack = useCallback(() => {
    if (editingContactId) {
      setEditingContactId(null);
      return;
    }
    if (contactBookOpen) {
      setContactBookOpen(false);
      return;
    }
    if (selectedRef.current) {
      setSelected(null);
      setShowList(true);
      if (hasHistoryPushedRef.current) {
        window.history.back();
      }
      hasHistoryPushedRef.current = false;
    }
  }, [editingContactId, contactBookOpen]);

  useEffect(() => {
    const handlePopState = () => {
      if (selectedRef.current) {
        setSelected(null);
        setShowList(true);
        hasHistoryPushedRef.current = false;
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Listen for ESC key globally to close modals or back out of active chat
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBack]);

  useEffect(() => {
    if (selected) document.body.classList.add('chat-open');
    else document.body.classList.remove('chat-open');
    return () => document.body.classList.remove('chat-open');
  }, [selected]);

  const openContact = async (c) => {
    if (!hasHistoryPushedRef.current) {
      window.history.pushState({ waChatOpen: true }, '');
      hasHistoryPushedRef.current = true;
    }
    // Reset messages when switching to a different contact to avoid showing previous chat
    if (selectedRef.current?.id !== c.id) {
      setMessages([]);
    }
    setSelected(c);
    setShowList(false);
    if (c.unread > 0) {
      setContacts(prev => prev.map(item => item.id === c.id ? { ...item, unread: 0 } : item));
      setAssignedUnreadCount(prev => Math.max(0, prev - (c.unread || 1)));
      API.post(`/whatsapp/contacts/${c.id}/read`).catch(() => {});
      window.dispatchEvent(new CustomEvent('chat-read'));
    }
    try {
      const r = await API.get(`/whatsapp/messages/${c.id}`);
      // Only commit if this contact is still active
      if (selectedRef.current?.id === c.id) {
        setMessages(r.data.messages || []);
      }
      if (r.data.contact && selectedRef.current?.id === c.id) {
        setSelected(r.data.contact);
      }
      loadContacts();
      fetchAssignedUnread();
      window.dispatchEvent(new CustomEvent('chat-read'));
    } catch { toast.error('Failed to load messages'); }
  };

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll messages for currently opened contact with fast 1.2s polling and immediate fetch
  useEffect(() => {
    if (!selected) return;
    const activeContactId = selected.id;
    const fetchActiveMsgs = async () => {
      try {
        const r = await API.get(`/whatsapp/messages/${activeContactId}`);
        if (selectedRef.current?.id !== activeContactId) return;
        const serverMsgs = r.data.messages || [];
        setMessages(prev => {
          const serverIds = new Set(serverMsgs.map(m => m.id));
          // Strictly retain only pending messages that belong to THIS active contact!
          const stillPending = prev.filter(m => m._pending && m.contact_id === activeContactId && !serverIds.has(m.id));
          return [...serverMsgs, ...stillPending];
        });
        if (r.data.contact && selectedRef.current?.id === activeContactId) {
          setSelected(prev => ({ ...prev, ...r.data.contact }));
        }
      } catch { /* ignore */ }
    };
    fetchActiveMsgs();
    const t = setInterval(fetchActiveMsgs, 1200);
    return () => clearInterval(t);
  }, [selected?.id]);

  const fetchContactBook = async (searchQuery = '') => {
    setLoadingBook(true);
    try {
      const res = await API.get('/whatsapp/contact-book', { params: { search: searchQuery } });
      setBookContacts(res.data.contacts || []);
    } catch {
      toast.error('Failed to load contact book');
    } finally {
      setLoadingBook(false);
    }
  };

  const openContactBook = () => {
    setContactBookOpen(true);
    fetchContactBook(bookSearch);
  };

  const handleSaveContactName = async (cId, newName) => {
    if (!newName || !newName.trim()) return;
    try {
      await API.put(`/whatsapp/contacts/${cId}`, { name: newName.trim() });
      toast.success('Contact saved to Contact Book');
      setBookContacts(prev => prev.map(c => c.id === cId ? { ...c, name: newName.trim() } : c));
      setContacts(prev => prev.map(c => c.id === cId ? { ...c, name: newName.trim() } : c));
      if (selected?.id === cId) {
        setSelected(prev => ({ ...prev, name: newName.trim() }));
      }
      setEditingContactId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update contact');
    }
  };

  const send = async () => {
    if (!selected) return;
    if (!text.trim() && !file) return;

    const targetContactId = selected.id;
    const outgoingText = text.trim();
    const outgoingFile = file;

    // Immediately clear input and file to eliminate any feeling of delay or lag
    setText('');
    setFile(null);
    if (textareaRef.current) textareaRef.current.style.height = '38px';
    if (inputRef.current) inputRef.current.value = '';

    // Create optimistic message immediately
    const tempId = `opt-${Date.now()}`;
    const mediaKind = outgoingFile ? (
      outgoingFile.type?.startsWith('image') ? 'image' :
      outgoingFile.type?.startsWith('video') ? 'video' :
      outgoingFile.type?.startsWith('audio') ? 'audio' : 'document'
    ) : 'text';
    const senderName = user?.name || user?.username || (user?.email ? user.email.split('@')[0] : 'You');
    const senderRole = user?.role || 'staff';

    const optimisticMsg = {
      id: tempId,
      contact_id: targetContactId,
      phone: selected.phone,
      direction: 'outgoing',
      type: mediaKind,
      text: outgoingText,
      media_url: outgoingFile ? URL.createObjectURL(outgoingFile) : null,
      sent_by: user?.id,
      sent_by_name: senderName,
      sent_by_role: senderRole,
      sender_name: senderName,
      created_at: new Date().toISOString(),
      _pending: true,
    };

    // Render immediately in chat only if current chat is the target chat
    if (selectedRef.current?.id === targetContactId) {
      setMessages(prev => [...prev, optimisticMsg]);
      setTimeout(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 40);
    }

    // Update contacts list preview immediately
    setContacts(prev => prev.map(c => c.id === targetContactId ? {
      ...c,
      last_message: outgoingText || `[${mediaKind}]`,
      last_time: optimisticMsg.created_at
    } : c));

    setSending(true);
    const fd = new FormData();
    if (outgoingText) fd.append('text', outgoingText);
    if (outgoingFile) fd.append('file', outgoingFile);

    try {
      const r = await API.post(`/whatsapp/reply/${targetContactId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data.ok === false) toast.error('WhatsApp API rejected the message');
      
      // Update message state only if target contact is currently open
      if (selectedRef.current?.id === targetContactId) {
        if (r.data?.message) {
          const confirmedMsg = r.data.message;
          setMessages(prev => prev.map(m => m.id === tempId ? { ...confirmedMsg, _pending: false } : m));
        } else {
          setMessages(prev => prev.map(m => m.id === tempId ? { ...m, _pending: false } : m));
        }
      }
      loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
      if (selectedRef.current?.id === targetContactId) {
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, _pending: false, _failed: true } : m));
      }
    } finally {
      setSending(false);
    }
  };

  const closeChat = async () => {
    if (!selected) return;
    try {
      await API.post(`/whatsapp/contacts/${selected.id}/close`);
      toast.success('Chat closed');
      if (hasHistoryPushedRef.current) {
        hasHistoryPushedRef.current = false;
        window.history.back();
      } else {
        setSelected(null);
        setShowList(true);
      }
      loadContacts();
    } catch { toast.error('Failed'); }
  };

  const reopenChat = async () => {
    if (!selected) return;
    try {
      await API.post(`/whatsapp/contacts/${selected.id}/reopen`);
      toast.success('Chat reopened');
      setTab('open');
      loadContacts();
    } catch { toast.error('Failed'); }
  };

  const handleAssign = async (adminId) => {
    if (!selected) return;
    try {
      const fd = new FormData();
      fd.append('admin_id', adminId || '');
      await API.post(`/whatsapp/contacts/${selected.id}/assign`, fd);
      toast.success(adminId ? 'Assigned' : 'Unassigned');
      setSelected(u => u ? { ...u, assigned_to_admin_id: adminId || null } : null);
      loadContacts();
      fetchAssignedUnread();
    } catch { toast.error('Failed to assign'); }
  };

  const filtered = contacts.filter(c => {
    if (tab === 'assigned') {
      if (!c.assigned_to_admin_id) return false;
    } else if (tab === 'unassigned') {
      if (c.assigned_to_admin_id) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.phone && c.phone.toLowerCase().includes(s)) || (c.name && c.name.toLowerCase().includes(s));
  }).sort((a, b) => {
    const aUnread = (selectedRef.current?.id === a.id) ? 0 : (a.unread > 0 ? 1 : 0);
    const bUnread = (selectedRef.current?.id === b.id) ? 0 : (b.unread > 0 ? 1 : 0);
    if (aUnread !== bUnread) return bUnread - aUnread;
    return (b.last_time || '').localeCompare(a.last_time || '');
  });

  const onKey = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter: line change / newline without sending
        return;
      }
      // Enter without Shift: sends message
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.nativeEvent?.isComposing) {
        e.preventDefault();
        send();
      }
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 min-w-0 w-full h-full rounded-none md:rounded-lg overflow-hidden border-0 md:border border-border bg-card" data-testid="whatsapp-chat-page">
      {/* Red Alert Banner for Inactive WhatsApp Numbers (> 1 hour without incoming messages) */}
      {inactivityAlerts.has_red_alerts && (
        <div className="bg-red-600 text-white px-3.5 py-2 text-xs flex items-center justify-between gap-3 shadow-sm shrink-0 animate-pulse">
          <div className="flex items-center gap-2 min-w-0">
            <Warning size={18} weight="fill" className="shrink-0 text-white" />
            <span className="truncate">
              <strong>RED ALERT:</strong>{' '}
              {inactivityAlerts.apis.filter(a => a.is_red_alert).map(a => `${a.display_name} (+${a.phone_number_id}) [No incoming msgs for ${a.silent_duration}]`).join(' • ')}
              {' — Number may be banned or disconnected. Please check status immediately!'}
            </span>
          </div>
          <a
            href="/app/whatsapp-apis"
            className="shrink-0 font-bold underline text-[11px] bg-white/20 hover:bg-white/30 px-2.5 py-1 rounded transition-colors"
          >
            Check Status →
          </a>
        </div>
      )}

      <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden w-full">
        {/* Contact list */}
        <div className={`${selected ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-[320px] lg:w-[340px] border-r border-border bg-background shrink-0 min-h-0 min-w-0`}>
          <div className="p-3 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <WhatsappLogo size={22} weight="fill" className="text-green-500" />
                <span className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>WhatsApp</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={openContactBook}
                className="h-7 text-xs gap-1.5 border-green-500/30 text-green-700 dark:text-green-400 hover:bg-green-500/10"
                title="Open WhatsApp Contact Book"
                data-testid="wa-contact-book-btn"
              >
                <AddressBook size={14} weight="duotone" />
                <span>Contact Book</span>
              </Button>
            </div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <input
                data-testid="wa-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search phone or name"
                className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md bg-muted border border-transparent focus:outline-none focus:border-primary"
              />
            </div>
            <div className="grid grid-cols-3 gap-1 mt-2">
              <button data-testid="wa-tab-open" onClick={() => setTab('open')} className={`text-xs py-1.5 rounded font-medium transition-colors ${tab==='open' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>Open</button>
              <button data-testid="wa-tab-unassigned" onClick={() => setTab('unassigned')} className={`text-xs py-1.5 rounded font-medium transition-colors ${tab==='unassigned' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>Unassigned</button>
              <button data-testid="wa-tab-assigned" onClick={() => setTab('assigned')} className={`text-xs py-1.5 rounded font-medium transition-colors flex items-center justify-center gap-1 ${tab==='assigned' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                <span>Assigned</span>
                {assignedUnreadCount > 0 && (
                  <span className="px-1.5 py-0.2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] text-center">
                    {assignedUnreadCount}
                  </span>
                )}
              </button>
              <button data-testid="wa-tab-closed" onClick={() => setTab('closed')} className={`text-xs py-1.5 rounded font-medium transition-colors ${tab==='closed' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>Closed</button>
              <button data-testid="wa-tab-hidden" onClick={() => setTab('hidden')} className={`text-xs py-1.5 rounded font-medium transition-colors ${tab==='hidden' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>Hidden</button>
              <button data-testid="wa-tab-blocked" onClick={() => setTab('blocked')} className={`text-xs py-1.5 rounded font-medium transition-colors ${tab==='blocked' ? 'bg-red-500 text-white' : 'bg-muted text-muted-foreground hover:text-red-500'}`}>Blocked</button>
            </div>
          </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-10 px-4">No {tab} chats.</div>
          ) : filtered.map(c => (
            <button
              key={c.id}
              data-testid={`wa-contact-${c.id}`}
              onClick={() => openContact(c)}
              className={`w-full text-left px-3 py-2.5 border-b border-border/60 hover:bg-accent flex items-center gap-3 ${selected?.id===c.id ? 'bg-accent' : ''}`}
            >
              <div className="relative w-11 h-11 rounded-full bg-green-500/15 flex items-center justify-center text-green-700 dark:text-green-400 font-semibold shrink-0">
                {(c.name || c.phone || '?').slice(0,2).toUpperCase()}
                <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-green-500 border-2 border-background flex items-center justify-center">
                  <WhatsappLogo size={9} weight="fill" className="text-white" />
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-sm font-medium truncate">{c.name || c.phone}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0">{fmtTime(c.last_time)}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  {c.api_title && <span className="text-[9px] text-green-700 dark:text-green-300 bg-green-500/10 px-1 rounded truncate max-w-[100px]">{c.api_title}</span>}
                  <WhatsApp24hListBadge lastIncomingTime={c.last_incoming_time || c.last_time} />
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground truncate">{c.last_message || '—'}</span>
                  {c.unread > 0 && <span className="bg-green-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center font-bold">{c.unread}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className={`${!selected ? 'hidden md:flex' : 'flex'} flex-col flex-1 min-w-0 min-h-0 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><rect fill=%22%23ece5dd%22 width=%22120%22 height=%22120%22/></svg>')] dark:bg-[#0b141a]`}>
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-6 text-center">
            <div>
              <ChatCircleText size={48} className="mx-auto text-green-500 mb-2" />
              Select a WhatsApp chat to start messaging
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 z-30 px-2.5 sm:px-3 py-2 border-b border-border bg-[#f0f2f5] dark:bg-[#202c33] flex items-center justify-between gap-1.5 sm:gap-2 min-h-[52px]">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <button
                  className="md:hidden p-1.5 -ml-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 active:scale-95 touch-manipulation text-foreground flex items-center justify-center shrink-0"
                  onClick={handleBack}
                  data-testid="wa-back-btn"
                  aria-label="Back to contact list (Esc)"
                  title="Back (Esc)"
                >
                  <ArrowLeft size={22} weight="bold" />
                </button>
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-green-500/20 flex items-center justify-center text-green-700 dark:text-green-400 text-sm font-semibold shrink-0">
                  {(selected.name || selected.phone).slice(0,2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  {inlineEditing ? (
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        await handleSaveContactName(selected.id, inlineName);
                        setInlineEditing(false);
                      }}
                      className="flex items-center gap-1"
                    >
                      <input
                        type="text"
                        value={inlineName}
                        onChange={e => setInlineName(e.target.value)}
                        className="text-xs px-2 py-0.5 rounded border border-primary bg-background focus:outline-none max-w-[120px] sm:max-w-[180px]"
                        autoFocus
                      />
                      <button type="submit" className="p-1 rounded bg-green-500 text-white hover:bg-green-600 transition-colors" title="Save">
                        <Check size={12} weight="bold" />
                      </button>
                      <button type="button" onClick={() => setInlineEditing(false)} className="p-1 rounded border border-border text-muted-foreground hover:bg-muted transition-colors" title="Cancel">
                        <X size={12} />
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs sm:text-sm font-semibold truncate">{selected.name || selected.phone}</span>
                      <button
                        onClick={() => {
                          setInlineName(selected.name || selected.phone || '');
                          setInlineEditing(true);
                        }}
                        title="Edit name"
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 shrink-0"
                      >
                        <PencilSimple size={13} />
                      </button>
                    </div>
                  )}
                  <div className="text-[10px] sm:text-[11px] text-muted-foreground flex items-center gap-1 truncate mt-0.5">
                    <span>+{selected.phone}</span>
                    {selected.api_title && <span className="px-1 bg-green-500/10 text-green-700 dark:text-green-300 rounded text-[9px] truncate max-w-[90px]">{selected.api_title}</span>}
                  </div>
                </div>
              </div>

              {/* Action buttons on right */}
              <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                <select
                  value={selected.assigned_to_admin_id || ''}
                  onChange={(e) => handleAssign(e.target.value)}
                  className="bg-transparent border border-border text-[11px] rounded px-1.5 py-1 text-muted-foreground hover:bg-accent max-w-[75px] sm:max-w-[120px] truncate"
                >
                  <option value="">-- Unassigned --</option>
                  {adminList.map(a => (
                    <option key={a.id} value={a.id}>{a.label || a.name}</option>
                  ))}
                  {user?.id && <option value={user.id}>Myself ({user.name})</option>}
                </select>
                {selected.assigned_to_admin_id && (
                  <Button size="sm" variant="ghost" onClick={() => handleAssign('')} className="hidden sm:inline-flex text-xs h-7 text-muted-foreground hover:text-foreground">
                    Unassign
                  </Button>
                )}

                {/* Super Admin Actions: Block User & Delete Contact */}
                {user?.role === 'super_admin' && (
                  <>
                    {selected.is_blocked ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleUnblockUser}
                        disabled={blockingUser}
                        data-testid="wa-unblock-btn"
                        className="hidden sm:inline-flex h-7 sm:h-8 text-xs gap-1 border-amber-500/40 text-amber-600 hover:bg-amber-500/10 px-2"
                      >
                        <ShieldCheck size={14} />
                        <span className="hidden sm:inline">Unblock</span>
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmBlockOpen(true)}
                        disabled={blockingUser}
                        data-testid="wa-block-btn"
                        className="hidden sm:inline-flex h-7 sm:h-8 text-xs gap-1 border-red-500/40 text-red-600 hover:bg-red-500/10 px-2"
                      >
                        <Prohibit size={14} />
                        <span className="hidden sm:inline">Block</span>
                      </Button>
                    )}
                  </>
                )}

                {/* Delete Chat option - Super Admin or Admin with delete_chat permission */}
                {canDeleteChat && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmDeleteOpen(true)}
                    disabled={deletingContact}
                    data-testid="wa-delete-contact-btn"
                    title="Permanently delete chat & messages"
                    className="hidden md:inline-flex h-7 sm:h-8 text-xs gap-1 border-red-500/40 text-red-600 hover:bg-red-500/10 px-2"
                  >
                    <Trash size={14} />
                    <span className="hidden lg:inline">Delete Chat</span>
                  </Button>
                )}

                {/* Close window X button (desktop only) */}
                <button
                  onClick={handleHideChatView}
                  data-testid="close-wa-chat-btn"
                  title="Hide chat"
                  className="hidden md:flex p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                >
                  <XCircle size={19} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-1.5 chat-scroll-container overscroll-contain min-h-0">
              {messages.map((m, i) => {
                const prev = messages[i-1];
                const showDate = !prev || fmtDate(prev.created_at) !== fmtDate(m.created_at);
                const isOut = m.direction === 'outgoing';
                return (
                  <div key={m.id}>
                    {showDate && (
                      <div className="text-center my-3">
                        <span className="text-[11px] px-2.5 py-1 rounded-md bg-white/70 dark:bg-black/40 text-gray-700 dark:text-gray-300">{fmtDate(m.created_at)}</span>
                      </div>
                    )}
                    <div className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[88%] sm:max-w-[75%] rounded-lg px-2.5 py-1.5 shadow-sm transition-opacity ${isOut ? 'bg-[#dcf8c6] dark:bg-[#005c4b] text-black dark:text-white' : 'bg-white dark:bg-[#202c33] text-black dark:text-white'} ${m._pending ? 'opacity-70' : ''}`}>
                        {/* Admin / Staff Sender Name Header on outgoing messages */}
                        {isOut && (m.sent_by_name || m.sender_name || (typeof m.sent_by === 'string' && !m.sent_by.includes('-') && m.sent_by)) && (
                          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800 dark:text-emerald-300 mb-1 border-b border-black/10 dark:border-white/10 pb-0.5">
                            <span className="opacity-75">👤</span>
                            <span>{m.sent_by_name || m.sender_name || m.sent_by}</span>
                            {(m.sent_by_role || m.role) && (
                              <span className="text-[9px] uppercase px-1 py-0.2 rounded bg-black/10 dark:bg-white/15 font-mono font-normal">
                                {m.sent_by_role || m.role}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Rich WhatsApp Media Handler */}
                        {(() => {
                          const mediaUrl = resolveMediaUrl(m.file_url || m.media_url, m.id);
                          const rawType = (m.media_type || m.type || '').toLowerCase();
                          const isImg = rawType === 'image' || rawType === 'photo' || rawType === 'sticker' || (mediaUrl && /\.(jpg|jpeg|png|webp|gif|svg|bmp)$/i.test(mediaUrl.split('?')[0]));
                          const isVid = rawType === 'video' || (mediaUrl && /\.(mp4|mov|webm|3gp)$/i.test(mediaUrl.split('?')[0]));
                          const isAud = rawType === 'audio' || rawType === 'voice' || (mediaUrl && /\.(mp3|ogg|wav|m4a|aac)$/i.test(mediaUrl.split('?')[0]));
                          const isDoc = rawType === 'document' || rawType === 'file' || rawType === 'doc' || (!isImg && !isVid && !isAud && (mediaUrl || m.has_media));

                          if (isImg || (!mediaUrl && m.has_media && !isVid && !isAud && !isDoc) || (!m.text && !mediaUrl && !isVid && !isAud && !isDoc && (rawType === 'image' || rawType === 'photo'))) {
                            return (
                              <WhatsAppImageBubble
                                mediaUrl={mediaUrl}
                                m={m}
                                setPreviewMedia={setPreviewMedia}
                                onRetry={handleRetryMedia}
                              />
                            );
                          }
                          if (isVid) {
                            if (!mediaUrl) {
                              return (
                                <div className="my-1.5 p-2 rounded-lg bg-black/5 dark:bg-white/5 border border-dashed border-emerald-500/30 max-w-[280px] flex items-center justify-between gap-2">
                                  <span className="text-xs text-foreground font-semibold">🎥 Video</span>
                                  <button onClick={() => handleRetryMedia(m.id)} className="text-[11px] text-emerald-600 font-semibold px-2 py-0.5 rounded bg-emerald-500/10">Load</button>
                                </div>
                              );
                            }
                            return (
                              <div className="my-1 max-w-[280px]">
                                <video src={mediaUrl} controls preload="metadata" className="rounded-lg max-w-full max-h-[280px]" />
                              </div>
                            );
                          }
                          if (isAud) {
                            if (!mediaUrl) {
                              return (
                                <div className="my-1.5 p-2 rounded-lg bg-black/5 dark:bg-white/5 border border-dashed border-emerald-500/30 max-w-[260px] flex items-center justify-between gap-2">
                                  <span className="text-xs text-foreground font-semibold">🎵 Audio</span>
                                  <button onClick={() => handleRetryMedia(m.id)} className="text-[11px] text-emerald-600 font-semibold px-2 py-0.5 rounded bg-emerald-500/10">Load</button>
                                </div>
                              );
                            }
                            return (
                              <div className="my-1 max-w-[260px]">
                                <audio src={mediaUrl} controls className="w-full" />
                              </div>
                            );
                          }
                          if (isDoc) {
                            const docName = m.file_name || (mediaUrl ? mediaUrl.split('/').pop() : 'Document');
                            const docExt = docName.split('.').pop()?.toUpperCase()?.slice(0, 4) || 'DOC';
                            return (
                              <div className="my-1.5 p-2 rounded-lg bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 max-w-[280px] flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-md bg-emerald-600/10 dark:bg-emerald-400/20 text-emerald-700 dark:text-emerald-300 flex items-center justify-center font-bold text-[11px] shrink-0">
                                  {docExt}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-foreground truncate" title={docName}>{docName}</p>
                                  {mediaUrl ? (
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <a
                                        href={mediaUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-0.5"
                                      >
                                        Open <ArrowSquareOut size={12} />
                                      </a>
                                      <span className="text-muted-foreground text-[10px]">•</span>
                                      <a
                                        href={mediaUrl}
                                        download={docName}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-0.5"
                                      >
                                        Download <DownloadSimple size={12} />
                                      </a>
                                    </div>
                                  ) : (
                                    <button onClick={() => handleRetryMedia(m.id)} className="text-[11px] text-emerald-600 font-semibold mt-0.5 hover:underline">
                                      Load document
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {m.text ? (
                          <div className="text-[14px] whitespace-pre-wrap break-words">{m.text}</div>
                        ) : (!m.has_media && !m.file_url && !m.media_url && (
                          <div className="text-[13px] text-muted-foreground italic">[Message]</div>
                        ))}
                        <div className="flex items-center justify-end gap-1 text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                          <span>{fmtTime(m.created_at)}</span>
                          {m._pending && <span className="text-[9px] italic opacity-75">sending...</span>}
                          {m._failed && <span className="text-[9px] text-red-500 font-bold">failed</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Expired Window Warning Alert */}
            <WhatsApp24hExpiredAlert isExpired={selectedTimer.isExpired} />

            {/* Composer */}
            {selected.status === 'closed' ? (
              <div className="sticky bottom-0 z-30 p-3 border-t border-border bg-[#f0f2f5] dark:bg-[#202c33] text-center text-xs text-muted-foreground pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
                This chat is closed. Reopen it to reply.
              </div>
            ) : (
              <div className="sticky bottom-0 z-30 p-2 sm:p-2.5 border-t border-border bg-[#f0f2f5] dark:bg-[#202c33] flex items-end gap-1.5 sm:gap-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]">
                <label className="cursor-pointer mb-0.5 rounded-full w-9 h-9 flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 touch-manipulation shrink-0" title="Attach">
                  <Paperclip size={20} className="text-muted-foreground" />
                  <input ref={inputRef} type="file" className="hidden" onChange={e => setFile(e.target.files[0])} data-testid="wa-file-input" />
                </label>
                {file && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-background text-xs max-w-[140px] truncate shrink-0 mb-1">
                    <span className="truncate">{file.name}</span>
                    <button onClick={() => setFile(null)}><X size={12} /></button>
                  </div>
                )}
                <textarea
                  ref={textareaRef}
                  rows={1}
                  data-testid="wa-input"
                  value={text}
                  onChange={e => {
                    setText(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(Math.max(e.target.scrollHeight, 38), 120)}px`;
                  }}
                  onKeyDown={onKey}
                  placeholder={selectedTimer.isExpired ? "Type a template message (24h window expired)" : "Type a message..."}
                  className="flex-1 resize-none bg-background rounded-2xl px-3.5 py-2 text-sm border border-border/80 focus:border-green-500 focus:outline-none min-h-[38px] max-h-[120px] overflow-y-auto leading-relaxed msg-input"
                  style={{ height: '38px' }}
                />
                <button
                  data-testid="wa-send-btn"
                  onClick={send}
                  disabled={sending || (!text.trim() && !file)}
                  className="w-9 h-9 mb-0.5 rounded-full bg-green-500 text-white flex items-center justify-center disabled:opacity-50 hover:bg-green-600 active:scale-95 transition-all touch-manipulation shrink-0 shadow-sm"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </div>

      {/* Contact Book Dialog */}
      <Dialog open={contactBookOpen} onOpenChange={setContactBookOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AddressBook size={22} className="text-green-500" />
              WhatsApp Contact Book
            </DialogTitle>
          </DialogHeader>

          <div className="relative my-2">
            <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-muted-foreground" />
            <Input
              placeholder="Search contact by name or phone..."
              value={bookSearch}
              onChange={e => {
                setBookSearch(e.target.value);
                fetchContactBook(e.target.value);
              }}
              className="pl-9 text-sm"
              data-testid="contact-book-search-input"
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 mt-2 pr-1 max-h-[50vh]">
            {loadingBook ? (
              <div className="text-center py-8 text-sm text-muted-foreground">Loading contacts...</div>
            ) : bookContacts.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">No contacts found in Contact Book.</div>
            ) : (
              bookContacts.map(c => {
                const isEditing = editingContactId === c.id;
                return (
                  <div
                    key={c.id}
                    className="p-3 rounded-lg border border-border/70 hover:border-green-500/40 bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-green-500/15 text-green-700 dark:text-green-400 flex items-center justify-center font-bold shrink-0">
                        <UserCircle size={24} weight="duotone" />
                      </div>
                      <div className="min-w-0 flex-1">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingName}
                              onChange={e => setEditingName(e.target.value)}
                              className="h-8 text-sm max-w-[200px]"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              className="h-8 px-2 text-xs"
                              onClick={() => handleSaveContactName(c.id, editingName)}
                            >
                              <Check size={14} /> Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-xs"
                              onClick={() => setEditingContactId(null)}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm truncate">{c.name || 'Unnamed Contact'}</span>
                            <button
                              onClick={() => {
                                setEditingContactId(c.id);
                                setEditingName(c.name || '');
                              }}
                              className="text-muted-foreground hover:text-foreground p-0.5 rounded"
                              title="Edit Name"
                            >
                              <PencilSimple size={13} />
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">+{c.phone}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <WhatsApp24hListBadge lastIncomingTime={c.last_incoming_time || c.last_time} />
                          {c.total_messages !== undefined && (
                            <span className="text-[10px] bg-muted px-1.5 py-0.2 rounded text-muted-foreground font-mono">
                              💬 {c.total_messages} msgs
                            </span>
                          )}
                          {c.last_time && (
                            <span className="text-[10px] text-muted-foreground">
                              Active: {fmtDate(c.last_time)} {fmtTime(c.last_time)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      <Button
                        size="sm"
                        variant="default"
                        className="h-8 text-xs gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => {
                          setContactBookOpen(false);
                          openContact(c);
                        }}
                      >
                        <WhatsappLogo size={14} weight="fill" /> Open Chat
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Resolution Media Preview Modal */}
      {previewMedia && (
        <Dialog open={Boolean(previewMedia)} onOpenChange={() => setPreviewMedia(null)}>
          <DialogContent className="max-w-3xl p-4 bg-background border border-border">
            <DialogHeader className="flex flex-row items-center justify-between pb-2 border-b">
              <DialogTitle className="text-sm truncate font-medium">
                {previewMedia.name || 'Photo Preview'}
              </DialogTitle>
              <a
                href={previewMedia.url}
                download={previewMedia.name || 'photo'}
                target="_blank"
                rel="noreferrer"
                className="text-xs px-2.5 py-1 rounded bg-primary text-primary-foreground flex items-center gap-1 font-medium hover:opacity-90 transition-opacity"
              >
                <DownloadSimple size={14} /> Download
              </a>
            </DialogHeader>
            <div className="flex items-center justify-center min-h-[300px] max-h-[75vh] overflow-auto py-3">
              <img src={previewMedia.url} alt="" className="max-w-full max-h-[70vh] object-contain rounded-md shadow-md" />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Contact Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash size={18} /> Delete WhatsApp Contact
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2 text-sm text-muted-foreground">
            <p>
              Are you sure you want to permanently delete{' '}
              <strong className="text-foreground">{selected?.name || `+${selected?.phone}`}</strong>?
            </p>
            <p className="text-xs text-red-500 bg-red-500/10 p-2.5 rounded border border-red-500/20">
              ⚠️ <strong>Warning:</strong> This will permanently erase this contact and all conversation history from the database. This action cannot be undone.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t">
            <Button variant="outline" size="sm" onClick={() => setConfirmDeleteOpen(false)} disabled={deletingContact}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDeleteContact} disabled={deletingContact} className="gap-1.5" data-testid="confirm-delete-contact-btn">
              {deletingContact ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block User Confirmation Dialog */}
      <Dialog open={confirmBlockOpen} onOpenChange={setConfirmBlockOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Prohibit size={18} /> Block WhatsApp User
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2 text-sm text-muted-foreground">
            <p>
              Are you sure you want to block{' '}
              <strong className="text-foreground">+{selected?.phone}</strong> ({selected?.name || 'User'})?
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 p-2.5 rounded border border-amber-500/20">
              ⛔ <strong>Notice:</strong> Once blocked, any future incoming messages from this phone number will be silently dropped and ignored by the server.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t">
            <Button variant="outline" size="sm" onClick={() => setConfirmBlockOpen(false)} disabled={blockingUser}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleBlockUser} disabled={blockingUser} className="gap-1.5" data-testid="confirm-block-user-btn">
              {blockingUser ? 'Blocking...' : 'Block User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
